import { serverMediatedPage } from '../../shared/reading_request';
import TranscriptHandler from './transcript_handler';
import SpeechmaticsHandler from './speechmatics_handler';
import { determineModel, isDeepgram, isSpeechmatics, isSonioxRealtime } from './constants';
import RealtimeSttConnection, { realtimeUrlFor, buildRealtimeError } from './realtime_stt_connection';
import PcmStreamTap, { int16ChunksToWavBlob, int16SampleCount, trimSilentEdges, REALTIME_SAMPLE_RATE } from './pcm_stream_tap';
import TranscriptionErrorReporter from './transcription_error_reporter';
import chatMetricsClient from '../../services/ChatMetricsClient';
import { acquireMicStream, classifyMicError } from './mic_capture';
import { bindHoldToRecord } from './hold_to_record';
import { liveTranscriptParts, renderLiveTranscript } from './live_transcript';
import { SttTokenProvider } from './stt_token';

// The longest an upload waits for a token that is not cached yet (LAN-1309).
const STT_TOKEN_UPLOAD_WAIT_MS = 500;

// The worker's own finalize budget is 6 s (STT_REALTIME_FINALIZE_TIMEOUT_MS),
// so 8 s only fires when the answer never arrives at all.
const REALTIME_FINALIZE_TIMEOUT_MS = 8000;

class AudioRecordingService {
  constructor(csrfToken, chatService, audioPlayerService, recordBtn, micIcon, pauseBtn, chatOptions = {}) {
    this.chatService = chatService;
    this.audioPlayerService = audioPlayerService;
    this.chatOptions = chatOptions;
    this.chatSubmitBtn = chatService.chatSubmitBtn;
    this.chatInput = chatService.chatInput;
    this.addChatLoader = chatService.addChatLoader;
    this.removeChatLoader = chatService.removeChatLoader;
    this.csrfToken = csrfToken;
    this.language = chatService.language;
    this.languageCode = chatService.languageCode;
    this.dialectCode = chatService.dialectCode;
    // battery

    this.recordBtn = recordBtn;
    this.micIcon = micIcon;
    this.pauseBtn = pauseBtn;

    this.micAccessGranted = false;
    this.vadSetup = false;
    this.mediaRecorder;
    this.audioChunks = [];
    this.isRecording = this.chatService.isRecording;
    this.timer;
    this.myvad;
    this.microphone = null;
    this.userSelectedMicrophone = false;
    this.deviceListStale = false;

    this.voxtralEnabled = chatService.chatOptions.voxtralDefaultEnabled || false;
    this.sonioxEnabled = chatService.chatOptions.sonioxDefaultEnabled || false;
    this.sonioxRealtimeEnabled = chatService.chatOptions.sonioxRealtimeEnabled || false;
    this.model = determineModel(chatService.chatOptions.transcriptionMode, this.voxtralEnabled, this.sonioxEnabled, this.sonioxRealtimeEnabled);
    this.useDeepgram = isDeepgram(chatService.chatOptions.transcriptionMode);
    this.serverMediated = serverMediatedPage();
    this.recordedSttEnabled = this.serverMediated && document.getElementById('chat-form')?.dataset.recordedSttEnabled === 'true';
    if (this.serverMediated) { this.model = 'cartus-recorded'; this.useDeepgram = false; }
    // LAN-1309: pages that send audio to the STT worker fetch its token now, so
    // the first recording does not wait for it.
    this.sttTokens = this.serverMediated ? null : new SttTokenProvider();
    if (this.sttTokens) this.sttTokens.get();
    this.transcriptHandler = new TranscriptHandler(this.chatInput);
    this.pauseBtnClicked = false;
    this.recordBtnClicked = false;
    this.cancelled = false;
    this.suggestionBtn = chatService.suggestionBtn;
    this.suggestionBtnOriginalContent = null;
    this.suggestionBtnCancelHandler = null;
    this.reconnectionAttempts = 0;
    this.maxReconnectionAttempts = 5;
    this.reconnectionDelay = 2000; // 2 seconds
    this.isReconnecting = false;
    this.isIntentionalDisconnect = false;
    this.keepAlive = null;

    this.speechmaticsHandler = null;
    this.listenersAttached = false;

    // Real-time Soniox transport (LAN-584). Null except between startRecording
    // and the stop that finishes the utterance.
    this.realtimeConnection = null;
    this.realtimeTap = null;
    this.realtimeFinishing = false;
    // Set when the socket did not open in time. The tap kept the audio, so the
    // utterance still goes up — over the POST path instead.
    this.realtimeFallbackReason = null;
    // True between onSpeechStart and the end of onSpeechEnd. stopRecording()
    // reads it to decide whether an utterance is still on its way to
    // onSpeechEnd or whether it has to finalise the socket itself.
    this.vadSpeechActive = false;
    // What the input box shows from the start of a recording until its
    // transcript lands: null when idle, else { state, text, finalText, mirror }.
    // mirror: the Deepgram/Speechmatics arms write into the input themselves.
    this.liveTranscript = null;
    this.recordingTimer = null;
  }

  microphoneAvailable() {
    return !this.serverMediated || this.recordedSttEnabled === true;
  }

  async getMicrophone() {
    if (!this.microphoneAvailable()) return false;
    if (this.microphone !== null) {
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = devices.filter(device => device.kind === 'audioinput');

      if (audioDevices.length > 0) {
        this.microphone = audioDevices[0].deviceId;
        this.userSelectedMicrophone = false;

        if (this.chatService.chatOptions.selectedMicrophone !== this.microphone) {
          this.chatService.chatOptions.selectedMicrophone = this.microphone;
        }
      } else {
        console.error('No audio input devices found');
        this.microphone = null;
      }
    } catch (error) {
      console.error('Error getting microphone:', error);
      this.microphone = null;
    }
  }

  async updateMicrophone(deviceId) {
    if (!this.microphoneAvailable()) return false;
    if (this.isRecording) {
      this.isRecording = false;
      this.chatService.isRecording = false;
      clearTimeout(this.timer);
      this.resetRecordingControls();
    }

    this.teardownRecorder();
    // The tap holds an AudioContext on the stream whose tracks are stopped
    // below. It has to go with the recorder, or it stays wired to a dead track.
    this.cancelRealtimeSession();

    if (this.myvad) {
      if (typeof this.myvad.pause === 'function') this.myvad.pause();
      if (typeof this.myvad.destroy === 'function') this.myvad.destroy();
      this.myvad = null;
    }
    // Detach before stopping: on an implementation that fires `ended`
    // synchronously from stop(), the watcher would otherwise re-acquire mid-switch.
    this.detachMicTrackWatch();
    if (this.stream) this.stream.getTracks().forEach(track => track.stop());
    // A stopped stream must not linger on `this.stream`: setup() treats any
    // stream as live and would re-enable recording against dead tracks.
    this.stream = null;

    this.microphone = deviceId;
    this.userSelectedMicrophone = Boolean(deviceId && deviceId !== 'default');
    this.vadSetup = false;
    const result = await acquireMicStream({ mediaDevices: navigator.mediaDevices, deviceId });
    if (result.failure) {
      this.micAccessGranted = false;
      this.trackMicFailure(result);
      this.openModal(result.failure);
      this.disableRecordBtn();
      return false;
    }
    this.stream = result.stream;
    this.watchMicStream(this.stream);
    const tracks = this.stream && typeof this.stream.getAudioTracks === 'function' ? this.stream.getAudioTracks() : [];
    if (tracks[0] && tracks[0].muted) {
      this.micAccessGranted = false;
      this.vadSetup = false;
      this.openModal({ messageKey: 'javascript.chat.mic_muted_html', permissionDenied: false });
      this.disableRecordBtn();
      return false;
    }
    if (result.usedDefaultDevice) this.handleDefaultDeviceFallback();
    await this.setupAudio(this.stream);
    if (this.useDeepgram || this.model === 'speechmatics') {
      this.setupBasicRecorder(this.stream);
    } else {
      await this.setupVAD(this.stream);
    }
    // A switch that follows a failed one must restore what the failure took
    // away, in every transcription mode.
    this.micAccessGranted = true;
    this.enableRecordBtn();
    return true;
  }

  // acquireMicStream can silently land on the browser default whenever a
  // device-specific request fails. Both entry points (a page-load access check
  // and an explicit switch) must recover the same way: forget the dead device,
  // put the dropdown back on "default", and say so. Capture stays enabled -
  // this is an informational recovery, not a permission error.
  handleDefaultDeviceFallback() {
    this.microphone = null;
    this.userSelectedMicrophone = false;
    const chatOptions = this.chatService?.chatOptions;
    if (chatOptions) chatOptions.selectedMicrophone = 'default';
    if (typeof chatOptions?.resetMicrophoneSelection === 'function') chatOptions.resetMicrophoneSelection();
    this.openModal({ messageKey: 'javascript.chat.mic_default_fallback', permissionDenied: false });
  }

  teardownRecorder() {
    if (this.mediaRecorder) {
      this.mediaRecorder.removeEventListener('dataavailable', this.dataAvailableHandler);
      this.mediaRecorder.removeEventListener('stop', this.stopRecordingHandler);
      if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    }
    this.mediaRecorder = null;
    this.audioChunks = [];
  }

  // Browsers cap concurrent AudioContexts, so every context we open must be closed again.
  async closeAudioContext() {
    const audioContext = this.audioContext;
    this.audioContext = null;
    await this.closeContext(audioContext);
  }

  async closeContext(audioContext) {
    if (!audioContext || typeof audioContext.close !== 'function') return;
    if (audioContext.state === 'closed') return;
    try {
      await audioContext.close();
    } catch (error) {
      console.error('Error closing audio context:', error);
    }
  }

  // Autoplay policies and backgrounded tabs suspend the context; recording is silent until it resumes.
  async resumeAudioContext() {
    const audioContext = this.audioContext;
    if (!audioContext || typeof audioContext.resume !== 'function') return false;
    if (audioContext.state !== 'suspended') return false;
    try {
      await audioContext.resume();
      return true;
    } catch (error) {
      console.error('Error resuming audio context:', error);
      return false;
    }
  }

  micTrackOf(stream) {
    if (!stream || typeof stream.getAudioTracks !== 'function') return null;
    const tracks = stream.getAudioTracks();
    return (tracks && tracks[0]) || null;
  }

  // A Bluetooth handoff or an OS default-device change ends the track; nothing else notices.
  watchMicStream(stream) {
    const track = this.micTrackOf(stream);
    if (!track || this.micTrack === track) return;

    this.detachMicTrackWatch();
    this.micTrack = track;
    this.micTrackEndedHandler = () => { this.handleMicTrackEnded(); };
    if (typeof track.addEventListener === 'function') {
      track.addEventListener('ended', this.micTrackEndedHandler);
    } else {
      track.onended = this.micTrackEndedHandler;
    }
  }

  detachMicTrackWatch() {
    const track = this.micTrack;
    const handler = this.micTrackEndedHandler;
    if (track && handler) {
      if (typeof track.removeEventListener === 'function') track.removeEventListener('ended', handler);
      if (track.onended === handler) track.onended = null;
    }
    this.micTrack = null;
    this.micTrackEndedHandler = null;
  }

  handleMicTrackEnded() {
    if (this.reacquiringMicStream) return Promise.resolve(false);
    this.reacquiringMicStream = true;
    return this.reacquireMicStream()
      .catch((error) => {
        console.error('Error re-acquiring microphone:', error);
        return false;
      })
      .then((reacquired) => {
        this.reacquiringMicStream = false;
        return reacquired;
      });
  }

  // Rebuilds the capture graph on the same device the user had selected.
  async reacquireMicStream() {
    if (!this.microphoneAvailable()) return false;
    this.detachMicTrackWatch();

    if (this.isRecording) {
      this.isRecording = false;
      this.chatService.isRecording = false;
      clearTimeout(this.timer);
      this.resetRecordingControls();
    }

    this.teardownRecorder();
    // Same reason as updateMicrophone: the tap is wired to the track that just
    // ended, and nothing else drops it.
    this.cancelRealtimeSession();
    this.destroyVad();
    this.stopStreamTracks();
    // The tracks above are now stopped. Drop the reference as well: setup() treats
    // any stream as live, so a failed re-acquisition would otherwise leave the
    // recorder wired to dead tracks.
    this.stream = null;
    this.vadSetup = false;

    const result = await acquireMicStream({ mediaDevices: navigator.mediaDevices, deviceId: this.microphone });
    if (result.failure) {
      this.micAccessGranted = false;
      this.trackMicFailure(result);
      // Tell the user why the button went away; the mic vanished without a replacement.
      this.openModal(result.failure);
      this.disableRecordBtn();
      return false;
    }

    this.stream = result.stream;
    this.watchMicStream(this.stream);
    // Same recovery as a page-load or manual switch: forget the dead device,
    // put the dropdown back on "default", and tell the user capture moved.
    if (result.usedDefaultDevice) this.handleDefaultDeviceFallback();

    await this.setupAudio(this.stream);
    if (this.useDeepgram || this.model === 'speechmatics') {
      this.setupBasicRecorder(this.stream);
    } else {
      await this.setupVAD(this.stream);
    }
    this.micAccessGranted = true;
    this.enableRecordBtn();
    return true;
  }

  destroyVad() {
    if (!this.myvad) return;
    if (typeof this.myvad.pause === 'function') this.myvad.pause();
    if (typeof this.myvad.destroy === 'function') this.myvad.destroy();
    this.myvad = null;
  }

  stopStreamTracks() {
    if (!this.stream || typeof this.stream.getTracks !== 'function') return;
    this.stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (error) {
        console.error('Error stopping microphone track:', error);
      }
    });
  }

  registerAudioLifecycleListeners() {
    if (!this.microphoneAvailable()) return false;
    if (this.audioLifecycleListenersAttached) return;
    this.audioLifecycleListenersAttached = true;

    this.deviceChangeHandler = () => { this.handleDeviceChange(); };
    this.visibilityChangeHandler = () => {
      if (document.visibilityState !== 'hidden') this.resumeAudioContext();
    };

    const mediaDevices = navigator.mediaDevices;
    if (mediaDevices && typeof mediaDevices.addEventListener === 'function') {
      mediaDevices.addEventListener('devicechange', this.deviceChangeHandler);
    }
    if (typeof document.addEventListener === 'function') {
      document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }
  }

  removeAudioLifecycleListeners() {
    const mediaDevices = navigator.mediaDevices;
    if (this.deviceChangeHandler && mediaDevices && typeof mediaDevices.removeEventListener === 'function') {
      mediaDevices.removeEventListener('devicechange', this.deviceChangeHandler);
    }
    if (this.visibilityChangeHandler && typeof document.removeEventListener === 'function') {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    this.deviceChangeHandler = null;
    this.visibilityChangeHandler = null;
    this.audioLifecycleListenersAttached = false;
  }

  handleDeviceChange() {
    if (this.isRecording) {
      this.deviceListStale = true;
      return Promise.resolve(false);
    }
    return this.refreshDeviceCache();
  }

  // getMicrophone() caches the first enumerated device forever; drop the cache so it re-enumerates.
  async refreshDeviceCache() {
    this.deviceListStale = false;
    if (this.userSelectedMicrophone) return false;
    this.microphone = null;
    await this.getMicrophone();
    return true;
  }

  // handleDeviceChange() defers a devicechange that arrives mid-recording.
  // Replay it once capture stops, otherwise the cached device list stays stale forever.
  flushDeferredDeviceRefresh() {
    if (!this.deviceListStale) return;
    Promise.resolve(this.refreshDeviceCache())
      .catch((error) => { console.error('Error refreshing microphone list:', error); });
  }

  async teardown() {
    this.unbindHoldToRecord?.();
    this.unbindHoldToRecord = null;
    this.cancelRealtimeSession();
    this.detachMicTrackWatch();
    this.removeAudioLifecycleListeners();
    this.teardownRecorder();
    this.destroyVad();
    this.stopStreamTracks();
    this.stream = null;
    this.vadSetup = false;
    await this.closeAudioContext();
  }

  async checkMicAccess() {
    if (!this.microphoneAvailable()) return false;
    try {
      await this.getMicrophone();
      const result = await acquireMicStream({ mediaDevices: navigator.mediaDevices, deviceId: this.microphone });
      if (result.failure) {
        this.micAccessGranted = false;
        this.trackMicFailure(result);
        this.openModal(result.failure);
        return false;
      }
      const stream = result.stream;
      this.stream = stream;
      this.watchMicStream(stream);

      if (stream.getAudioTracks()[0].muted) {
        this.micAccessGranted = false;

        this.openModal({ messageKey: 'javascript.chat.mic_muted_html', permissionDenied: false });
        this.disableRecordBtn();
        return false;
      } else {
        // Report the fallback before enabling capture. Without this the
        // dropdown keeps naming a device we are not recording from.
        if (result.usedDefaultDevice) this.handleDefaultDeviceFallback();
        this.vadSetup = false; // force VAD re-init with fresh stream
        await this.setupAudio(stream);
        await this.setupRecordBtn();
        return true;
      }

    } catch (error) {
      this.micAccessGranted = false;
      const failure = classifyMicError(error);
      this.trackMicFailure({ failure, stage: 'setup', audioInputCount: 0 });
      this.openModal(failure);
      return false;
    }
  }

  trackMicFailure(result) {
    const ua = navigator.userAgent || '';
    const browserFamily = /Edg\//.test(ua) ? 'edge' : /Chrome\//.test(ua) ? 'chrome' : /Firefox\//.test(ua) ? 'firefox' : /Safari\//.test(ua) ? 'safari' : 'other';
    const osFamily = /Windows/.test(ua) ? 'windows' : /Android/.test(ua) ? 'android' : /iPhone|iPad|iPod/.test(ua) ? 'ios' : /Mac OS/.test(ua) ? 'macos' : /Linux/.test(ua) ? 'linux' : 'other';
    chatMetricsClient.track({ event: 'mic_capture_failure', error_name: result.failure.name, stage: result.stage, browser_family: browserFamily, os_family: osFamily, audio_input_count: result.audioInputCount });
  }

  // Preserves the audio output device when the user grants mic access
  async setupAudio(stream) {
    try {
      await this.closeAudioContext();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.audioContext = audioContext;
      const destination = audioContext.createMediaStreamDestination();

      const audioElement = new Audio();
      audioElement.srcObject = destination.stream;

      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioOutputDevices = devices.filter(device => device.kind === 'audiooutput');
      const audioInputDevices = devices.filter(device => device.kind === 'audioinput');

      if (typeof audioElement.setSinkId === 'function') {
        if (audioOutputDevices.length > 0) {
          await audioElement.setSinkId(audioOutputDevices[0].deviceId);
        } else if (audioInputDevices.length > 0) {
          await audioElement.setSinkId(audioInputDevices[0].deviceId);
        } else {
          console.log("Error setting up audio - no devices found");
        }

        audioElement.play();
      }
    } catch (error) {
      console.error("Error setting up audio:", error);
    }
  }

  buildModal(failure = { messageKey: 'javascript.chat.mic_blocked', permissionDenied: true }) {
    const modalHTML = `
      <div class="modal fade" id="micErrorModal" tabindex="-1" role="dialog" aria-labelledby="chatOptionsModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="chatOptionsModalLabel">${I18n.t(failure.messageKey)}</h5>
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body">
              ${failure.permissionDenied ? `<p>${I18n.t('javascript.chat.mic_instructions')}</p>
              <p>${I18n.t('javascript.chat.mic_refresh_hint')}</p>` : ''}
              <p><a href="https://support.languatalk.com/article/148-im-experiencing-issues-with-the-ai-conversation-feature-communicate-what-should-i-do#I-cannot-record-myself-on-the-website-version-Htr9W" target="_blank">${I18n.t('javascript.chat.mic_help_link')}</a></p>
            </div>
          </div>
        </div>
      </div>
    `;

    // Append the modal HTML to the body
    document.body.insertAdjacentHTML('beforeend', modalHTML);
  }

  showModal() {
    $('#micErrorModal').modal('show');
  }

  destroyModal() {
    $('#micErrorModal').modal('dispose');
  }

  openModal(failure) {
    // Check if the modal already exists
    if (document.getElementById('micErrorModal')) {
      this.destroyModal();
    }

    this.buildModal(failure);
    this.showModal();
  }
  
  async setupRecordBtn() {
    const that = this;
    that.micAccessGranted = true;

    if (that.serverMediated) {
      if (!that.recordedSttEnabled) { that.disableRecordBtn(); return; }
      if (!that.vadSetup && !that.mediaRecorder) await that.setupVAD(that.stream);
      that.enableRecordBtn();
    } else if (that.useDeepgram) {
      await that.setupDeepgram();
      that.setupBasicRecorder(that.stream);
    } else {
      if (!that.vadSetup) {
        await that.setupVAD(that.stream);
        that.enableRecordBtn();
      }
    }

    // setup event listeners (only once to avoid duplicate handlers on recovery)
    if (!that.listenersAttached) {
      that.listenersAttached = true;
      that.recordBtn.addEventListener('click', that.recordBtnClick.bind(that));

      that.pauseBtn.addEventListener('click', that.pauseBtnClick.bind(that));

      that.unbindHoldToRecord = bindHoldToRecord({
        doc: document,
        win: window,
        recordBtn: () => that.recordBtn,
        chatInput: that.chatInput,
        isRecording: () => that.isRecording,
        canStart: () => that.micAccessGranted && !that.recordBtn.disabled && !document.querySelector('.modal.show')
      });

      // Listen to the playended custom event
      document.addEventListener('chat:audioended', () => {
        if (that.chatService.chatOptions.autoRecord && that.micAccessGranted) {
          that.startRecording();
        }
      });

      document.addEventListener('chat:audiostarted', () => {
        if (that.isRecording) {
          that.stopRecording();
        }
      });

      document.addEventListener('chat:pronounciationstarted', () => {
        if (that.isRecording) {
          that.stopRecording();
        }
      });
    }
  }

  pauseBtnClick(event) {
    // chat_service.js also listens on this button when it's acting as the keyboard toggle.
    // Only act here when recording; otherwise let chat_service handle the keyboard toggle.
    if (!this.isRecording) {
      return;
    }
    event.preventDefault();

    this.pauseBtnClicked = true;

    this.toggleRecording();

    if (!this.isRecording && this.chatInput.value.length > 0) {
      if (this.chatService && this.chatService.updateInputBarState) {
        this.chatService.updateInputBarState();
      }
    }
  }

  recordBtnClick(event) {
    event.preventDefault();

    if (this.isRecording) {
      this.recordBtnClicked = true;
    }

    this.toggleRecording();

    // Check for both Deepgram and Speechmatics
    if ((this.useDeepgram || this.model === 'speechmatics') && !this.isRecording && this.chatInput.value.length > 0) {
      if (this.chatService && this.chatService.updateInputBarState) {
        this.chatService.updateInputBarState();
      }
      this.chatSubmitBtn.click();
      // TODO: This is a temporary fix to clear the chat input after the button is clicked.
      // We need to find a better solution to handle this.
      setTimeout(() => {
        this.chatInput.value = '';
      }, 1000);
    }
  }

  toggleRecording() {
    const that = this;
    if (that.isRecording) {
      that.stopRecording();
    } else {
      if (that.audioPlayerService.soundPlaying && that.audioPlayerService.player.playing()) {
        that.audioPlayerService.togglePlayer();
      }
      that.startRecording();
    }
  }

  setupSuggestionButtonCancelState() {
    if (!this.suggestionBtn) {
      return;
    }

    if (this.suggestionBtnOriginalContent === null) {
      this.suggestionBtnOriginalContent = this.suggestionBtn.innerHTML;
    }

    if (this.suggestionBtnCancelHandler) {
      this.suggestionBtn.removeEventListener('click', this.suggestionBtnCancelHandler, true);
    }

    this.suggestionBtn.innerHTML = '<i class="fa fa-trash"></i>';
    this.suggestionBtnCancelHandler = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.cancelRecording();
      // Restore any text that existed before recording started
      this.chatInput.value = this.preRecordingText || '';
      this.chatInput.dispatchEvent(new Event('input', { bubbles: true }));
      if (this.chatService && this.chatService.updateInputBarState) {
        this.chatService.updateInputBarState();
      }
    };
    this.suggestionBtn.addEventListener('click', this.suggestionBtnCancelHandler, true);
  }

  restoreSuggestionButtonState() {
    if (!this.suggestionBtn) {
      return;
    }

    if (this.suggestionBtnCancelHandler) {
      this.suggestionBtn.removeEventListener('click', this.suggestionBtnCancelHandler, true);
      this.suggestionBtnCancelHandler = null;
    }

    if (this.suggestionBtnOriginalContent !== null) {
      this.suggestionBtn.innerHTML = this.suggestionBtnOriginalContent;
      this.suggestionBtnOriginalContent = null;
    }
  }

  showConnectingMessage() {
    if (!document.querySelector('.js-deepgram-connecting')) {
      this.recordBtn.insertAdjacentHTML('beforebegin', `<span class="js-deepgram-connecting" style="padding: 0.5rem 1rem; font-size: 1rem;">${I18n.t('javascript.chat.connecting')}</span>`);
    }
  }

  hideConnectingMessage() {
    const connectingMessage = document.querySelector('.js-deepgram-connecting');
    if (connectingMessage) {
      connectingMessage.remove();
    }
  }

  dismissMicModal(afterDismiss) {
    const micModal = document.getElementById('micErrorModal');
    if (micModal) {
      $(micModal).one('hidden.bs.modal', function () {
        $(this).remove();
        document.querySelectorAll('.modal-backdrop').forEach(el => el.remove());
        document.body.classList.remove('modal-open');
        if (afterDismiss) afterDismiss();
      });
      $(micModal).modal('hide');
    } else if (afterDismiss) {
      afterDismiss();
    }
  }

  showEnableMicAccessLink() {
    if (!this.microphoneAvailable()) return false;
    const that = this;
    // check if the enable mic access link already exists
    if (!document.querySelector('.js-enable-mic-access')) {
      that.recordBtn.insertAdjacentHTML('afterend', `<a href="#" class="js-enable-mic-access btn btn-link">${I18n.t('javascript.chat.enable_mic_access')}</a>`);
      document.querySelector('.js-enable-mic-access').addEventListener('click', async function (e) {
        e.preventDefault();
        that.dismissMicModal();
        const hasMicAccess = await that.checkMicAccess();
        if (hasMicAccess) {
          this.remove();
        }
      });
    }
  }

  disableRecordBtn() {
    const that = this;
    that.recordBtn.disabled = true;
    that.recordBtn.classList.add('d-none');
    that.pauseBtn.disabled = true;
    that.pauseBtn.classList.add('d-none');

    if (!that.microphoneAvailable()) return;

    if (that.isReconnecting) {
      that.showConnectingMessage();
    } else {
      that.showEnableMicAccessLink();
    }
  }

  enableRecordBtn() {
    if (!this.microphoneAvailable()) return false;
    this.recordBtn.disabled = false;
    this.recordBtn.classList.remove('d-none');
    this.pauseBtn.classList.remove('d-none');
    this.pauseBtn.disabled = false;
    if (this.useDeepgram) {
      this.hideConnectingMessage();
    }
  }

  trackMicrophonePermission() {
    if (!this.microphoneAvailable()) return false;
    const that = this;
    navigator.permissions.query({ name: 'microphone' })
      .then(function(permissionStatus) {
        // Listen for changes in the permission state
        permissionStatus.onchange = function() {
          if (this.state === 'denied') {
            that.disableRecordBtn();
          } else if (this.state === 'prompt') {
            that.disableRecordBtn();
          }
        };
      })
      .catch(function() {
        // permissions API not supported or denied — no action needed
      });
  }

  async setup({ deferPermission = false } = {}) {
    if (this.serverMediated && !this.recordedSttEnabled) { this.disableRecordBtn(); return; }
    if (!navigator || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.log('Missing support for navigator.mediaDevices.getUserMedia');
      return;
    }

    const that = this;

    that.trackMicrophonePermission();
    that.registerAudioLifecycleListeners();

    if (that.serverMediated && deferPermission && !that.stream) {
      // Text chat must work without opening a microphone permission dialog.
      // The existing enable-microphone link initializes capture on user action.
      that.disableRecordBtn();
      // disableRecordBtn also locks the keyboard toggle. In a text-only chat the
      // learner types, so keep the toggle usable and open the text box.
      that.pauseBtn.disabled = false;
      that.pauseBtn.classList.remove('d-none');
      that.chatService.keyboardToggleVisible = true;
      if (typeof that.chatService.updateInputBarState === 'function') that.chatService.updateInputBarState();
      return;
    }

    if (that.stream) {
      that.watchMicStream(that.stream);
      if (that.stream.getAudioTracks()[0].muted) {
        that.micAccessGranted = false;
        that.disableRecordBtn();

        that.openModal({ messageKey: 'javascript.chat.mic_muted_html', permissionDenied: false });
      } else {
        await that.setupAudio(that.stream);
        await that.setupRecordBtn();
      }
    } else {
      const hasMicAccess = await that.checkMicAccess();
      if (!hasMicAccess) that.disableRecordBtn();
      this.chatService.chatOptions.audioRecordingService = that;
    }
  }

  async setupVAD(stream) {
    const that = this;

    try {
      if (typeof vad !== 'undefined' && vad.MicVAD) {
        that.myvad = await vad.MicVAD.new({
          stream,
          redemptionFrames: that.chatService.chatOptions.autoSend ? 30 : 240,
          submitUserSpeechOnPause: true,
          onSpeechStart: () => {
            that.vadSpeechActive = true;
            that.recordBtn.innerHTML = `<i class="fa fa-paper-plane"></i>`;
            that.recordBtn.classList.add('btn-chat-send');
            that.recordBtn.classList.remove('btn-chat-record');
          },
          // vad-web fires this instead of onSpeechEnd when the utterance was
          // shorter than minSpeechFrames, and it hands over no audio. A short
          // answer ("si") is still an answer, and the tap has it, so finish
          // the turn rather than drop it. Cancelling here would lose it.
          onVADMisfire: () => {
            that.vadSpeechActive = false;
            if (!isSonioxRealtime(that.model)) {
              // After stop, a misfire is the end of the turn: no audio follows.
              if (!that.isRecording) that.endLiveTranscript();
              return;
            }
            if (that.cancelled) {
              that.cancelled = false;
              return;
            }
            if (that.isRecording) {
              that.stopRecording();
            } else {
              that.finishRealtimeSession();
            }
          },
          onSpeechEnd: async (audio) => {
            // vad-web 0.0.16 discards this promise, so a rejection here would be
            // lost entirely. Catch it and show the user the same message a
            // failed upload shows (LAN-380), and always close the per-utterance
            // AudioContext (LAN-381).
            let audioContext = null;
            try {
              if (that.cancelled) {
                that.cancelled = false;
                return;
              }

              if (that.isRecording) {
                that.stopRecording();
              }

              // The real-time arm has already streamed this utterance. Ask the
              // worker to finalise it instead of encoding and uploading it.
              // Only when a session exists: a tap that failed to open leaves
              // this arm with nothing to finalise, and the ordinary upload
              // below is what saves the utterance.
              if (isSonioxRealtime(that.model) && (that.hasRealtimeSession() || that.realtimeFinishing)) {
                await that.finishRealtimeSession();
                return;
              }

              audioContext = new (window.AudioContext || window.webkitAudioContext)();
              const audioBuffer = audioContext.createBuffer(1, audio.length, 16000);
              const channelData = audioBuffer.getChannelData(0);
              channelData.set(audio);

              const audioBlob = await new Promise((resolve) => {
                that.audioBufferToWavBlob(audioBuffer, (blob) => {
                  resolve(blob);
                });
              });

              const sent = that.sendDataToRailsBackend(audioBlob);
              if (sent && typeof sent.catch === 'function') {
                sent.catch((error) => that.handleTranscriptionFailure(error));
              }

              that.audioChunks = [];
            } catch (error) {
              that.handleSpeechEndFailure(error);
              that.handleTranscriptionFailure(error);
            } finally {
              // Cleared last, so the stopRecording() above still sees an open
              // segment and leaves the finalise to this handler.
              that.vadSpeechActive = false;
              await that.closeContext(audioContext);
            }
          }
        });

        that.vadSetup = true;
      } else {
        console.error('Error initializing VAD');
        that.vadSetup = false;
        if (that.serverMediated) return that.setupBasicRecorder(stream);
        that.setupBasicRecorder(stream);
      }
    } catch (error) {
      console.error('Error initializing VAD:', error);
      that.vadSetup = false;
      // Fallback to basic recorder without VAD
      if (that.serverMediated) return that.setupBasicRecorder(stream);
      that.setupBasicRecorder(stream);
    }
  }

  audioBufferToWavBlob(audioBuffer, callback) {
    const that = this;
    const audioData = audioBuffer.getChannelData(0);
    const buffer = new ArrayBuffer(44 + audioData.length * 2);
    const view = new DataView(buffer);
    const sampleRate = audioBuffer.sampleRate;
    const numChannels = audioBuffer.numberOfChannels;

    that.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + audioData.length * 2, true);
    that.writeString(view, 8, 'WAVE');
    that.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 4, true);
    view.setUint16(32, numChannels * 2, true);
    view.setUint16(34, 16, true);
    that.writeString(view, 36, 'data');
    view.setUint32(40, audioData.length * 2, true);

    that.floatTo16BitPCM(view, 44, audioData);

    const blob = new Blob([view], { type: 'audio/wav' });
    callback(blob);
  }

  writeString(view, offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  floatTo16BitPCM(output, offset, input) {
    for (let i = 0; i < input.length; i++, offset += 2) {
      const s = Math.max(-1, Math.min(1, input[i]));
      output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
  }

  async setupBasicRecorder(stream) {
    const that = this;

    if (that.serverMediated && window.MediaRecorder === undefined) {
      throw new Error('Recorded transcription requires browser audio recording support');
    }

    // use AudioRecorder polyfill if MediaRecorder is not supported
    if (window.MediaRecorder === undefined) {
      import(/* webpackChunkName: "AudioRecorder" */ 'audio-recorder-polyfill').then((AudioRecorderModule) => {
        import(/* webpackChunkName: "mpegEncoder" */ 'audio-recorder-polyfill/mpeg-encoder').then((mpegEncoderModule) => {
          const AudioRecorder = AudioRecorderModule.default;
          AudioRecorder.encoder = mpegEncoderModule.default;
          AudioRecorder.prototype.mimeType = 'audio/mpeg';
          that.mediaRecorder = new AudioRecorder(stream);

          that.setupMediaRecorder()
        });
      });
    } else {
      that.mediaRecorder = new MediaRecorder(stream);
      that.setupMediaRecorder()
    }
  }

  setupMediaRecorder() {
    const that = this;

    that.dataAvailableHandler = e => {
      if (that.useDeepgram && e.data.size > 0 && that.isRecording) {
        this.socket.send(e.data);
      } else if (that.model === 'speechmatics' && e.data.size > 0 && that.isRecording) {
        that.speechmaticsHandler.sendAudioChunk(e.data);
      }

      that.audioChunks.push(e.data);
    };

    that.stopRecordingHandler = e => {
      if (isSonioxRealtime(that.model) && (that.hasRealtimeSession() || that.realtimeFinishing)) {
        // The tap holds this utterance as PCM; the recorder's own blob is only
        // ever used by the upload path.
        that.finishRealtimeSession();
      } else if ((!that.serverMediated || !that.cancelled) && !that.useDeepgram && that.model !== 'speechmatics') {
        const mime = that.serverMediated ? (that.mediaRecorder.mimeType || that.audioChunks[0]?.type) : 'audio/mpeg';
        const audioBlob = new Blob(that.audioChunks, { type: mime });

        that.sendDataToRailsBackend(audioBlob);
      }

      that.audioChunks = []; // Reset audio chunks
    }

    that.mediaRecorder.addEventListener('dataavailable', that.dataAvailableHandler);

    that.mediaRecorder.addEventListener('stop', that.stopRecordingHandler);
  }

  resetAudioChunks() {
    this.audioChunks = [];
    if (this.mediaRecorder) {
      this.mediaRecorder.removeEventListener('dataavailable', this.dataAvailableHandler);
      this.mediaRecorder.removeEventListener('stop', this.stopRecordingHandler);
      this.setupMediaRecorder();
    }
  }

  // setup 60 second timer that gets cancelled if user stops recording before 60 seconds
  setupTimer() {
    const that = this;

    that.timer = setTimeout(function () {
      that.stopRecording();
    }, 60000);
  }

  async startRecording() {
    const that = this;
    if (that.serverMediated && !that.recordedSttEnabled) {
      that.handleTranscriptionFailure(new Error('Recorded transcription is not enabled'), 503);
      return;
    }

    await that.resumeAudioContext();

    that.isRecording = true;
    that.cancelled = false;
    that.chatService.isRecording = true;
    // Save any text that was in the input before recording started
    that.preRecordingText = that.chatInput.value || '';
    that.chatService.keyboardToggleVisible = false;
    that.chatService.persistKeyboardPreference(false);
    that.setupSuggestionButtonCancelState();

    if (that.model === 'speechmatics') {
      if (!this.speechmaticsHandler || !this.speechmaticsHandler.isConnected) {
        this.showConnectingMessage();
        await that.setupSpeechmatics();
        this.hideConnectingMessage();
      }

      if (!that.mediaRecorder) {
        that.setupBasicRecorder(that.stream);
      }

      if (this.chatInput.value.length === 0) {
        this.speechmaticsHandler.clearTranscript();
      }

      if (that.mediaRecorder.state === "paused") {
        that.mediaRecorder.resume();
      } else {
        that.mediaRecorder.start(100);
      }
      // Don't setup timer for Speechmatics
    } else {
      if (that.useDeepgram) {
        if (!this.deepgramConnected()) {
          await that.setupDeepgram();
          that.setupBasicRecorder(that.stream);
        }

        if (this.chatInput.value.length === 0) {
          this.transcriptHandler.clearTranscript();
        }

        if (that.mediaRecorder.state === "paused") {
          that.mediaRecorder.resume();
        } else {
          that.mediaRecorder.start(100);
        }
        that.setupTimer();
      } else {
        // The real-time arm streams the microphone while the learner is still
        // talking, so the socket and the PCM tap open alongside whichever
        // recorder drives the turn. Neither blocks capture: the tap buffers
        // from the first frame, and a socket that never opens only decides
        // which transport carries the utterance.
        if (isSonioxRealtime(that.model)) {
          that.startRealtimeSession();
        }

        if (this.vadSetup) {
          this.myvad.start();
          if (that.serverMediated) that.setupTimer();
        } else {
          await this.mediaRecorder.start();
          that.setupTimer();
        }
      }
    }

    that.pauseBtn.disabled = false;
    that.pauseBtn.classList.remove('d-none');
    that.recordBtn.innerHTML = `<i class="fa fa-paper-plane"></i>`;
    that.recordBtn.classList.remove('btn-chat-record');
    that.recordBtn.classList.add('btn-chat-send');
    that.pauseBtn.innerHTML = `<i class="fa fa-pause"></i>`;
    that.beginLiveTranscript();
  }
  
  async updateTranscriptionMode(transcriptionMode) {
    if (this.serverMediated) {
      this.model = 'cartus-recorded';
      this.useDeepgram = false;
      return this.setup();
    }
    this.recordBtn.replaceWith(this.recordBtn.cloneNode(true));
    this.recordBtn = document.getElementById("js-record-query-btn");
    // Sync ChatService reference so updateInputBarState() operates on the live node
    this.chatService.recordBtn = this.recordBtn;

    this.pauseBtn.replaceWith(this.pauseBtn.cloneNode(true));
    this.pauseBtn = document.getElementById("js-pause-recording-btn");
    this.chatService.pauseBtn = this.pauseBtn;
    this.chatService.bindPauseBtnListener();

    if (isDeepgram(transcriptionMode)) {
      this.useDeepgram = true;
      this.model = determineModel(transcriptionMode, this.voxtralEnabled, this.sonioxEnabled, this.sonioxRealtimeEnabled);

      if (!this.deepgramConnected()) {
        this.showConnectingMessage();
      }

      this.setupBasicRecorder(this.stream);
    } else if (isSpeechmatics(transcriptionMode)) {
      this.model = 'speechmatics';
      this.showConnectingMessage();
      await this.setupSpeechmatics();
      this.hideConnectingMessage();
    } else {
      this.useDeepgram = false;
      this.tearDownDeepgram();
      if (this.speechmaticsHandler) {
        await this.speechmaticsHandler.tearDown();
        this.speechmaticsHandler = null;
      }
      this.hideConnectingMessage();
      this.model = determineModel(transcriptionMode, this.voxtralEnabled, this.sonioxEnabled, this.sonioxRealtimeEnabled);
    }

    this.setup();
  }

  async getDeepgramApiKey() {
    if (this.serverMediated) throw new Error('Browser provider credentials are disabled');
    // Fetch API key
    const response = await fetch('/langua/deepgram_keys', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken
      },
      credentials: 'same-origin',
      body: JSON.stringify({ chat_id: this.chatService.chatId })
    })
    const data = await response.json();
    const apiKey = data.api_key;

    return apiKey;
  }

  deepgramConnected() {
    return this.deepgram && this.socket && this.socket.getReadyState() === 1;
  }

  async setupDeepgram() {
    if (this.serverMediated) throw new Error('Browser provider streaming is disabled');

    const that = this;

    if (that.deepgramConnected()) {
      return;
    }

    const { createClient } = deepgram;
    const apiKey = await that.getDeepgramApiKey();

    that.keepAlive = null;
    that.deepgram = createClient(apiKey);
    that.isIntentionalDisconnect = false;

    let language_or_dialect = (that.dialectCode && that.dialectCode.includes('en')) ? that.dialectCode : that.languageCode;

    that.socket = that.deepgram.listen.live({ 
      mip_opt_out: true,
      model: "nova-2",
      language: language_or_dialect,
      interim_results: true,
      smart_format: true,
      endpointing: 550,
      utterance_end_ms: 1500,
      filler_words: true,
      });

    that.socket.on("open", async () => {
      that.isReconnecting = false;
      that.enableRecordBtn();

      if (that.keepAlive !== null) clearInterval(that.keepAlive);
      that.keepAlive = setInterval(() => {
        that.socket.keepAlive();
      }, 3000);

      that.socket.on("Results", (data) => {
        if (that.isRecording) {
          that.transcriptHandler.processTranscript(data);
        }
      });

      that.socket.on("error", (e) => {
        console.error("Deepgram error:", e);
        that.handleDeepgramError(e);
      });

      that.socket.on("warning", (e) => console.warn(e));

      that.socket.on("Metadata", (e) => console.log(e));

      that.socket.on("close", (e) => {
        console.log("client: disconnected from websocket", e);
        clearInterval(that.keepAlive);
        that.handleDeepgramDisconnection();
      });
    });
  }

  handleDeepgramError(error) {
    console.error("Deepgram error:", error);
    this.handleDeepgramDisconnection();
  }

  handleDeepgramDisconnection() {
    if (this.isRecording) {
      this.stopRecording();
    }

    if (this.isIntentionalDisconnect) {
      return;
    }

    this.isReconnecting = true;
    this.disableRecordBtn();
    this.reconnectToDeepgram();
  }

  async reconnectToDeepgram() {
    if (this.isIntentionalDisconnect) {
      console.log("Intentional disconnect detected, cancelling reconnection.");
      this.isReconnecting = false;
      return;
    }

    if (this.reconnectionAttempts < this.maxReconnectionAttempts) {
      this.reconnectionAttempts++;
      console.log(`Attempting to reconnect to Deepgram (Attempt ${this.reconnectionAttempts})`);

      this.showConnectingMessage();

      setTimeout(async () => {
        try {
          await this.setupDeepgram();
        } catch (error) {
          console.error("Failed to reconnect to Deepgram:", error);
          this.reconnectToDeepgram(); // Try again
        }
      }, this.reconnectionDelay);
    } else {
      console.error("Max reconnection attempts reached. Please try again later.");
      this.isReconnecting = false;
      this.hideConnectingMessage();
      this.showEnableMicAccessLink();
    }
  }

  tearDownDeepgram() {
    this.isIntentionalDisconnect = true;
    if (this.deepgram && this.socket) {
      this.socket.finish();
      this.socket = null;
      this.deepgram = null;
    }
    if (this.keepAlive) {
      clearInterval(this.keepAlive);
      this.keepAlive = null;
    }
    this.isReconnecting = false;
    this.hideConnectingMessage();
  }

  sendDataToDeepgram(audioBlob) {
    const reader = new FileReader();
    reader.onloadend = () => {
      const arrayBuffer = reader.result;
      this.socket.send(arrayBuffer);
    };
    reader.readAsArrayBuffer(audioBlob);
  }

  resetRecordingControls() {
    this.restoreSuggestionButtonState();
    this.recordBtn.classList.remove('pulse');
    this.recordBtn.innerHTML = `<i class="fa fa-microphone"></i>`;
    this.recordBtn.classList.add('btn-chat-record');
    this.recordBtn.classList.remove('btn-chat-send');
    this.pauseBtn.classList.remove('d-none');
    this.pauseBtn.disabled = false;
    this.pauseBtn.innerHTML = `<i class="fa fa-keyboard-o"></i>`;
    clearTimeout(this.timer);

    if (this.chatService && this.chatService.updateInputBarState) {
      this.chatService.updateInputBarState();
    }
  }

  // LAN-380 owns the visible surface; this only guarantees the failure is observed.
  handleSpeechEndFailure(error) {
    console.error('Error handling end of speech:', error);
    this.isRecording = false;
    if (this.chatService) this.chatService.isRecording = false;
    this.endLiveTranscript();
    if (typeof this.resetRecordingControls === 'function') {
      try {
        this.resetRecordingControls();
      } catch (resetError) {
        console.error('Error restoring recording controls:', resetError);
      }
    }
  }

  cancelRecording() {
    this.isRecording = false;
    this.chatService.isRecording = false;
    this.cancelled = true;
    this.endLiveTranscript();
    this.resetRecordingControls();
    this.audioChunks = [];

    // Remove stop/dataavailable listeners BEFORE stopping the recorder
    // to prevent the stopRecordingHandler from firing and sending audio.
    this.resetAudioChunks();

    // The realtime socket and its tap sit beside the recorder branches below,
    // so cancel drops them whichever recorder is driving capture. A no-op when
    // there is no session.
    this.cancelRealtimeSession();

    if (this.model === 'speechmatics') {
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.isIntentionalDisconnect = true;
        this.mediaRecorder.stop();
      }
      if (this.speechmaticsHandler) {
        this.speechmaticsHandler.stopRecognition();
      }
    } else if (this.useDeepgram) {
      if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
        this.mediaRecorder.stop();
      }
      if (this.socket) {
        this.isIntentionalDisconnect = true;
        this.socket.finalize();
      }
    } else if (this.vadSetup) {
      if (this.myvad) {
        this.myvad.pause();
      }
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      }
    } else if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }

    this.flushDeferredDeviceRefresh();
  }

  stopRecording() {
    const awaitingTranscript = this.transcriptPendingAfterStop();
    this.isRecording = false;
    this.chatService.isRecording = false;
    if (awaitingTranscript) {
      this.finishLiveTranscript();
    } else {
      this.endLiveTranscript();
    }
    this.resetRecordingControls();

    if (this.model === 'speechmatics') {
      if (this.mediaRecorder.state === "recording") {
        // Pause the media recorder after 1 second to allow the final chunk to be sent
        setTimeout(() => {
          this.mediaRecorder.pause();
          this.speechmaticsHandler.finalize();
          this.resetAudioChunks();
        }, 1000);
      }
    } else if (this.useDeepgram) {
      if (this.mediaRecorder.state === "recording") {
        this.mediaRecorder.pause();
        this.resetAudioChunks();
        this.socket.finalize();
      }
    } else if (this.vadSetup) {
      this.myvad.pause();
      // Pausing the VAD is not the end of an utterance. onVADMisfire, or a
      // stop before the learner said anything, means onSpeechEnd never
      // arrives - and then nothing finalises the socket, closes the tap or
      // frees its AudioContext, and the next session starts on the stale refs.
      // The realtimeFinishing guard absorbs a concurrent onSpeechEnd.
      if (isSonioxRealtime(this.model) && !this.vadSpeechActive) {
        this.finishRealtimeSession();
      }
    } else if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      // The VAD failed to load and setupBasicRecorder took over. Without this
      // the recorder ran on after the learner pressed stop and
      // stopRecordingHandler never fired, so nothing was transcribed.
      this.mediaRecorder.stop();
    } else if (isSonioxRealtime(this.model)) {
      this.finishRealtimeSession();
    }

    this.flushDeferredDeviceRefresh();
  }

  playRecording(audioBlob) {
    const audioUrl = URL.createObjectURL(audioBlob);
    this.chatService.audioPlayerService.playSound(audioUrl, this.micIcon);
  }

  shouldBeSubmitted() {
    return this.recordBtnClicked || (this.chatService.chatOptions.autoSend && !this.pauseBtnClicked);
  }

  // Function to send audio data to Rails backend
  async sendDataToRailsBackend(audioBlob) {
    if (this.serverMediated && !this.recordedSttEnabled) {
      this.handleTranscriptionFailure(new Error('Recorded transcription is not enabled'), 503);
      return;
    }
    this.dismissTranscriptionError();
    this.addChatLoader(this.chatService.chatElement);
    this.recordBtn.disabled = true;
    this.pauseBtn.disabled = true;
    this.chatSubmitBtn.disabled = true;

    const formData = new FormData();
    formData.append("audio", audioBlob, "recorded_audio.wav");
    if (this.serverMediated) {
      const translateToNative = this.chatService.chatTopicCategory?.value?.startsWith('grammar') &&
        this.chatService.chatTopic?.value === 'translate_sentences_to_native';
      // ChatOptions names the learner's native language as the translation target.
      formData.append("language", translateToNative ? this.chatOptions.targetLanguage : this.language);
    } else {
      formData.append("language", this.language);
      formData.append("model", this.model);
      formData.append("dialect", this.dialectCode);

      // Transcription post-processing fields
      if (this.chatOptions.transcriptionCleanup) {
        formData.append("transcription_cleanup", "true");

        // Send the last AI message for context
        const previousMessage = this.lastAiMessageText();
        if (previousMessage) {
          formData.append("previous_message", previousMessage);
        }

        // Send whether this is a guided lesson
        formData.append("is_guided", this.isGuidedLesson() ? "true" : "false");

        // Selects the letter-matching cleanup prompt: multiple choice answers are
        // spoken as a single letter and come back as a similar sounding word.
        formData.append(
          "is_multiple_choice",
          this.chatOptions.multipleChoiceExercise ? "true" : "false"
        );
      }
    }

    try {
      let response;

      if (this.serverMediated) {
        if (!this.csrfToken || !/^[0-9a-f-]{36}$/i.test(this.chatService.chatId) || !audioBlob || audioBlob.size > 8 * 1024 * 1024) {
          throw new Error('Recording session or audio is invalid');
        }
        response = await fetch(`/langua/cartus_chats/${this.chatService.chatId}/transcription`, {
          method: 'POST', headers: { 'X-CSRF-Token': this.csrfToken, 'Accept': 'application/json' },
          credentials: 'same-origin', redirect: 'error',
          signal: AbortSignal.timeout(35000), body: formData
        });
      } else if (this.model === 'gemini') {
        response = await fetch('/langua/stream_speech_to_text', {
          method: "POST",
          headers: {
            'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'),
            'Accept': 'text/event-stream'
          },
          credentials: 'same-origin',
          body: formData
        });
      } else {
        // Get transcription URL from backend configuration
        const transcriptionUrl = this.chatOptions.transcriptionUrl || 'https://stt.languatalk.com';
        
        // Add user UUID header for per-user circuit breakers
        const headers = {};
        if (this.chatOptions.userUuid) {
          headers['X-User-Id'] = `user_${this.chatOptions.userUuid}`;
        }
        // A form field, not a header: a new header needs the worker's CORS
        // allow-list first, and this upload is live. LAN-1309.
        const sttToken = this.sttTokens ? await this.sttTokens.get(STT_TOKEN_UPLOAD_WAIT_MS) : null;
        if (sttToken) formData.append('stt_token', sttToken);
        
        response = await fetch(transcriptionUrl, {
          method: "POST",
          headers: headers,
          body: formData
        });
      }

      if (!response.ok) {
        // The request reached the service and was refused (4xx) or broke on the
        // way (5xx). Tell the user instead of failing silently. LAN-380.
        this.reportTranscriptionOutcome(
          TranscriptionErrorReporter.planOutcome({ status: response.status, hasText: false }),
          response.status
        );
        return;
      }

      // Create a temporary URL for the audio blob and store the audio URL temporarily until we get the message ID
      this.chatService.pendingAudioUrl = URL.createObjectURL(audioBlob);

      if (!this.serverMediated && this.model === 'gemini') {
        await this.handleStreamingResponse(response);
        
        // After streaming is complete
        this.recordBtn.disabled = false;
        this.pauseBtn.disabled = false;
        this.chatSubmitBtn.disabled = false;
        this.removeChatLoader(this.chatService.chatElement);
        
        if (this.shouldBeSubmitted()) {
          this.chatSubmitBtn.click();
        }
        // Submit button visibility handled by updateInputBarState() in finally block
      } else {
        // Existing non-streaming logic
        let data = null;
        try {
          data = await response.json();
        } catch (parseError) {
          data = null;
        }

        const hasText = Boolean(data) && data.text !== undefined;
        const outcome = TranscriptionErrorReporter.planOutcome({ status: response.status, hasText, code: data && data.code });

        if (outcome.failed) {
          // A 200 with no usable text used to fall off the end of this branch
          // and leave the record button disabled forever. LAN-380.
          this.reportTranscriptionOutcome(outcome, response.status);
          return;
        }

        this.applyTranscriptionResult(data);
        // Submit button visibility handled by updateInputBarState() in finally block
      }
    } catch (error) {
      // Network drop, aborted fetch, streaming failure: the user saw nothing
      // here before LAN-380.
      this.handleTranscriptionFailure(error);
    } finally {
      this.pauseBtnClicked = false;
      this.recordBtnClicked = false;
      if (this.chatService && this.chatService.updateInputBarState) {
        this.chatService.updateInputBarState();
      }
    }
  }

  // Both transports send the same cleanup context, so both read it from here.
  lastAiMessageText() {
    const aiMessages = document.querySelectorAll('.chat-message:not(.self) .chat-message-detail');
    const lastAiMessageEl = aiMessages.length > 0 ? aiMessages[aiMessages.length - 1] : null;
    return lastAiMessageEl ? lastAiMessageEl.textContent.trim() : null;
  }

  isGuidedLesson() {
    const topicCategoryEl = document.querySelector('#js-chat-topic-category');
    return (topicCategoryEl ? topicCategoryEl.value : null) === 'guided';
  }

  // The one place a finished transcript reaches the input box and decides
  // whether to submit. The upload path and the realtime transport both end
  // here, so the two arms cannot drift apart.
  applyTranscriptionResult(data) {
    this.dismissTranscriptionError();
    this.endLiveTranscript();
    const text = (data && data.text) || '';
    if (this.chatInput.value.length > 0) {
      this.chatInput.value = `${this.chatInput.value} ${text}`;
    } else {
      this.chatInput.value = text;
    }
    this.chatInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.recordBtn.disabled = false;
    this.pauseBtn.disabled = false;
    this.chatSubmitBtn.disabled = false;
    this.removeChatLoader(this.chatService.chatElement);
    if (!this.shouldBeSubmitted()) {
      this.chatService.keyboardToggleVisible = true;
    }
    if (this.shouldBeSubmitted()) {
      // Track audio submission
      const tempMessageId = `audio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      chatMetricsClient.trackMessageSubmitted(this.chatService.chatId, tempMessageId);
      this.chatSubmitBtn.click();
    }
  }

  // ---- Real-time Soniox transport (LAN-584) ---------------------------------
  // The learner's audio goes to the STT worker over a WebSocket while they are
  // still speaking, so the only latency left after they stop is the endpoint
  // tail. Everything here is written so that a failure costs latency and never
  // an utterance: the tap keeps the whole turn in memory, and every path that
  // does not produce a `result` uploads it over the existing POST instead.

  buildRealtimeStartMessage() {
    const message = {
      language: this.language,
      model: this.model,
      sample_rate: REALTIME_SAMPLE_RATE,
      dialect: this.dialectCode || ''
    };

    // No identity field: the worker takes the learner from the STT token
    // (LAN-1309) and ignores any identity the start frame claims.
    if (this.chatService.chatId) message.chat_id = String(this.chatService.chatId);

    if (this.chatOptions.transcriptionCleanup) {
      message.transcription_cleanup = true;
      const previousMessage = this.lastAiMessageText();
      if (previousMessage) message.previous_message = previousMessage;
      message.is_guided = this.isGuidedLesson();
      message.is_multiple_choice = Boolean(this.chatOptions.multipleChoiceExercise);
    }

    return message;
  }

  hasRealtimeSession() {
    return Boolean(this.realtimeConnection || this.realtimeTap);
  }

  startRealtimeSession() {
    // A finalise still in flight owns the tap and the socket. Auto-record can
    // start the next turn while the worker is still finishing this one.
    if (this.realtimeFinishing) return;
    if (this.hasRealtimeSession()) return;

    this.realtimeFallbackReason = null;

    const connection = new RealtimeSttConnection({
      url: realtimeUrlFor(this.chatOptions.transcriptionUrl),
      start: this.buildRealtimeStartMessage(),
      onPartial: (text, finalText) => this.handleRealtimePartial(text, finalText),
      onSettled: () => this.handleRealtimeSettled(connection),
      // The worker refuses a session without a valid token (LAN-1309); the
      // refusal is retryable, so the upload takes over.
      token: this.sttTokens && (this.sttTokens.current() || (() => this.sttTokens.get()))
    });
    const tap = new PcmStreamTap({
      stream: this.stream,
      sampleRate: REALTIME_SAMPLE_RATE,
      onChunk: (chunk) => connection.send(chunk)
    });

    this.realtimeConnection = connection;
    this.realtimeTap = tap;

    try {
      tap.start();
    } catch (error) {
      // Safari refuses an AudioContext at 16 kHz, a page can run out of
      // contexts, and a rejection here would reject startRecording() after
      // isRecording was already true: the VAD would never start and the
      // learner would record nothing. Drop the session and let the ordinary
      // upload path carry the turn.
      console.error('Realtime capture unavailable:', (error && error.message) || error);
      this.cancelRealtimeSession();
      this.realtimeFallbackReason = error;
      return;
    }

    // Deliberately not awaited: capture starts now, the handshake catches up.
    // If it does not, the tap has the audio and stop() falls back.
    connection.connect().catch((error) => {
      console.error('Realtime transcription unavailable:', (error && error.message) || error);
      this.realtimeFallbackReason = error;
    });
  }

  // The socket finished on its own — a worker error, or a close without a
  // result. While the learner is still talking the tap must stay, because
  // stopRecording() uploads what it holds; finishRealtimeSession() still
  // reaches the fallback then, because stop() on a settled connection returns
  // the result promise that already rejected. Once they have stopped, nothing
  // is waiting on this session, and leaving its refs in place would make the
  // next startRealtimeSession() a no-op.
  handleRealtimeSettled(connection) {
    if (this.realtimeFinishing) return;
    if (this.realtimeConnection !== connection) return;
    if (this.isRecording) return;
    this.cancelRealtimeSession();
  }

  // Display only: the partial goes to the read-only live box, never into the
  // input's value, so it cannot overwrite a typed draft or be submitted. The
  // result reaches the input once, through applyTranscriptionResult().
  handleRealtimePartial(text, finalText = '') {
    this.updateLiveTranscript(text, finalText);
  }

  // The worker has the audio but not for ever: a dead network after `stop`
  // would otherwise leave the loader up and every button disabled until the
  // page reloads. Retryable, so the existing fallback uploads the utterance.
  raceRealtimeFinalize(promise, connection) {
    // Instance override exists for tests: CI runs Node 18, which has no timer mocks.
    const deadlineMs = this.realtimeFinalizeTimeoutMs || REALTIME_FINALIZE_TIMEOUT_MS;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (connection) connection.cancel();
        reject(buildRealtimeError('finalize_timeout', 'Realtime transcription did not finish in time', true));
      }, deadlineMs);

      promise.then(
        (value) => { clearTimeout(timer); resolve(value); },
        (error) => { clearTimeout(timer); reject(error); }
      );
    });
  }

  async finishRealtimeSession() {
    if (this.realtimeFinishing) return;
    if (!this.hasRealtimeSession()) return;

    this.realtimeFinishing = true;
    const connection = this.realtimeConnection;
    const tap = this.realtimeTap;
    this.realtimeConnection = null;
    this.realtimeTap = null;

    this.dismissTranscriptionError();
    this.addChatLoader(this.chatService.chatElement);
    this.recordBtn.disabled = true;
    this.pauseBtn.disabled = true;
    this.chatSubmitBtn.disabled = true;

    let pcmChunks = [];
    try {
      if (tap) {
        // The tail is the batched-but-unsent blocks. It has to reach the
        // socket before `stop`, or the end of the utterance is transcribed
        // from nothing. LAN-381: the tap owns an AudioContext and browsers cap
        // how many a page may hold open.
        const tail = await tap.stop();
        if (tail && connection && !this.realtimeFallbackReason) connection.send(tail);
        pcmChunks = tap.takeBuffer();
      }

      // A handshake that never finished means this utterance was never on the
      // wire. Drop the socket and upload what the tap kept.
      if (!connection || this.realtimeFallbackReason) {
        if (connection) connection.cancel();
        this.uploadBufferedPcm(pcmChunks);
        return;
      }

      let result;
      try {
        result = await this.raceRealtimeFinalize(connection.stop(), connection);
      } catch (error) {
        console.error('Realtime transcription failed:', (error && error.message) || error);
        // A cancel is the learner's own doing; the UI is already back.
        if (error && error.code === 'cancelled') return;
        // retryable:false means the worker will refuse the same audio again.
        // Uploading it would only spend another provider call on a refusal.
        if (error && error.retryable === false) {
          this.handleTranscriptionFailure(error);
          return;
        }
        this.uploadBufferedPcm(pcmChunks);
        return;
      }

      this.applyRealtimeResult(result);
    } catch (error) {
      this.handleSpeechEndFailure(error);
      this.handleTranscriptionFailure(error);
    } finally {
      this.realtimeFinishing = false;
      this.realtimeFallbackReason = null;
    }
  }

  // The fallback, and the reason a broken socket is only ever a latency cost.
  // The live text stays in the box, marked as finishing, until the upload's
  // own transcript replaces it.
  uploadBufferedPcm(pcmChunks) {
    // Soniox async hallucinates on long silence, and the silence is upload
    // weight either way. What is left is the speech plus 300 ms each side.
    const trimmed = trimSilentEdges(pcmChunks, REALTIME_SAMPLE_RATE);
    if (int16SampleCount(trimmed) === 0) {
      // Nothing was captured at all. There is no audio to transcribe, so give
      // the learner exactly what an empty transcript gives them.
      this.applyRealtimeResult({ text: '' });
      return;
    }

    const audioBlob = int16ChunksToWavBlob(trimmed, REALTIME_SAMPLE_RATE);
    const sent = this.sendDataToRailsBackend(audioBlob);
    if (sent && typeof sent.catch === 'function') {
      sent.catch((error) => this.handleTranscriptionFailure(error));
    }
  }

  applyRealtimeResult(result) {
    this.applyTranscriptionResult(result);
    this.pauseBtnClicked = false;
    this.recordBtnClicked = false;
    if (this.chatService && this.chatService.updateInputBarState) {
      this.chatService.updateInputBarState();
    }
  }

  cancelRealtimeSession() {
    const connection = this.realtimeConnection;
    const tap = this.realtimeTap;
    if (!connection && !tap) return;

    this.realtimeConnection = null;
    this.realtimeTap = null;
    this.realtimeFinishing = false;
    this.realtimeFallbackReason = null;

    if (connection) connection.cancel();
    if (tap) {
      const closing = tap.stop();
      if (closing && typeof closing.catch === 'function') closing.catch(() => {});
    }
    this.endLiveTranscript();
  }

  // ---- Live input box (LAN-584) ----------------------------------------------
  // While the learner records, the composer stays open and its box shows the
  // draft plus the live transcript, read-only. chat_service.updateInputBarState
  // lays the bar out from this state; this section owns the state and the text.

  beginLiveTranscript() {
    this.liveTranscript = {
      state: 'listening',
      text: '',
      finalText: '',
      mirror: this.useDeepgram || this.model === 'speechmatics'
    };
    this.startRecordingTimer();
    this.renderLiveTranscript();
  }

  updateLiveTranscript(text, finalText = '') {
    if (!this.liveTranscript) return;
    this.liveTranscript.text = text || '';
    this.liveTranscript.finalText = finalText || '';
    this.renderLiveTranscript();
  }

  finishLiveTranscript() {
    this.stopRecordingTimer();
    if (!this.liveTranscript) return;
    this.liveTranscript.state = 'finishing';
    this.renderLiveTranscript();
  }

  endLiveTranscript() {
    this.stopRecordingTimer();
    if (!this.liveTranscript) return;
    this.liveTranscript = null;
    this.renderLiveTranscript();
  }

  // Whether stop leaves an utterance on its way to a transcript. Without one
  // (the learner stopped before speaking) the box must not wait in "finishing".
  transcriptPendingAfterStop() {
    if (!this.liveTranscript || this.liveTranscript.mirror) return false;
    if (isSonioxRealtime(this.model) && (this.hasRealtimeSession() || this.realtimeFinishing)) return true;
    if (this.vadSetup) return this.vadSpeechActive;
    return Boolean(this.mediaRecorder && this.mediaRecorder.state !== 'inactive');
  }

  renderLiveTranscript() {
    const box = this.chatService && this.chatService.liveTranscriptBox;
    if (box && this.liveTranscript && !this.liveTranscript.mirror) {
      renderLiveTranscript(box, liveTranscriptParts(
        { draft: this.preRecordingText || '', ...this.liveTranscript },
        { listening: I18n.t('javascript.chat.listening'), finishing: I18n.t('javascript.chat.transcribing') }
      ));
    }
    if (this.chatService && this.chatService.updateInputBarState) {
      this.chatService.updateInputBarState();
    }
  }

  startRecordingTimer() {
    this.stopRecordingTimer();
    const label = this.chatService && this.chatService.recordingTimeLabel;
    if (!label) return;
    const startedAt = Date.now();
    const show = () => {
      const seconds = Math.floor((Date.now() - startedAt) / 1000);
      label.textContent = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
    };
    show();
    this.recordingTimer = setInterval(show, 1000);
  }

  stopRecordingTimer() {
    if (this.recordingTimer) clearInterval(this.recordingTimer);
    this.recordingTimer = null;
  }

  // ---- Transcription error surface (LAN-380) --------------------------------
  // Before this, a failed transcription only reached console.error: the user
  // watched a loader vanish and got no reply. These four helpers put a visible,
  // dismissible message in the chat input area and always give the buttons back.

  // Entry point for a thrown error or a rejected promise anywhere on the
  // recording path. Status 0 means "never got an answer".
  handleTranscriptionFailure(error, status = 0) {
    if (error) {
      // Message only. Never the transcript, the audio, or the chat history.
      console.error('Transcription failed:', error.message || error);
    }
    this.reportTranscriptionOutcome(
      TranscriptionErrorReporter.planOutcome({ status, hasText: false }),
      status
    );
  }

  // Acts on a plan from TranscriptionErrorReporter.planOutcome().
  reportTranscriptionOutcome(outcome, status = 0) {
    if (!outcome) {
      return;
    }

    if (outcome.failed) {
      this.endLiveTranscript();
      console.error(
        'Transcription outcome:',
        TranscriptionErrorReporter.buildReport({
          status,
          transcriptionMode: this.chatOptions ? this.chatOptions.transcriptionMode : null,
          language: this.language
        })
      );
      this.showTranscriptionError(outcome.userMessage);
    }

    if (outcome.restoreControls) {
      this.restoreRecordingControls();
    }
  }

  // Give the microphone back. Called on every outcome, so a failure can never
  // leave the record button dead until the page is reloaded.
  restoreRecordingControls() {
    if (this.recordBtn) {
      this.recordBtn.disabled = false;
    }
    if (this.pauseBtn) {
      this.pauseBtn.disabled = false;
    }
    if (this.chatSubmitBtn) {
      this.chatSubmitBtn.disabled = false;
    }
    if (typeof this.removeChatLoader === 'function' && this.chatService) {
      this.removeChatLoader(this.chatService.chatElement);
    }
  }

  showTranscriptionError(message) {
    if (!message || typeof document === 'undefined' || !document.createElement) {
      return null;
    }

    this.dismissTranscriptionError();

    const anchor = document.querySelector('.js-chat-input-shell')
      || (this.chatInput && this.chatInput.parentNode)
      || document.querySelector('.chat-controls');
    if (!anchor) {
      return null;
    }

    const banner = document.createElement('div');
    banner.className = 'chat-transcription-error alert alert-warning d-flex align-items-center justify-content-between';
    banner.setAttribute('role', 'alert');

    const text = document.createElement('span');
    text.textContent = message;

    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'btn btn-link p-0 ml-2 chat-transcription-error-dismiss';
    dismiss.setAttribute('aria-label', 'Dismiss');
    dismiss.textContent = '×';
    if (dismiss.addEventListener) {
      dismiss.addEventListener('click', () => this.dismissTranscriptionError());
    }

    banner.appendChild(text);
    banner.appendChild(dismiss);

    if (anchor.parentNode && anchor.parentNode.insertBefore) {
      anchor.parentNode.insertBefore(banner, anchor);
    } else {
      anchor.appendChild(banner);
    }

    return banner;
  }

  dismissTranscriptionError() {
    if (typeof document === 'undefined' || !document.querySelectorAll) {
      return;
    }

    const banners = Array.prototype.slice.call(
      document.querySelectorAll('.chat-transcription-error')
    );
    banners.forEach((banner) => {
      if (banner.parentNode) {
        banner.parentNode.removeChild(banner);
      }
    });
  }

  async handleStreamingResponse(response) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let streamText = '';
 
    // Get the current input value as the starting point
    let currentText = this.chatInput.value;
 
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
 
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(5); // Remove "data: " prefix
              const data = JSON.parse(jsonStr);

              // Extract text from the structured response
              const { text, finish_reason, usage } = data;
 
            // Add new text to the stream accumulator
            streamText += text;
 
            // Update chat input with both previous text and new streamed text
            if (currentText.length > 0) {
              this.chatInput.value = `${currentText} ${streamText}`.trim();
            } else {
              this.chatInput.value = streamText.trim();
            }

            // If this is the final chunk
            if (finish_reason === 'STOP') {
              // Update currentText to include the completed stream
              currentText = this.chatInput.value;
              // Reset stream accumulator for potential next stream
              streamText = '';
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        }
      }
    }
  } catch (error) {
    console.error('Error reading stream:', error);
    throw error;
    }
  }

  async setupSpeechmatics() {
    if (this.serverMediated) throw new Error('Browser provider streaming is disabled');
    if (this.speechmaticsHandler) {
      return;
    }

    try {
      const response = await fetch('/langua/speechmatics_keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        credentials: 'same-origin',
        body: JSON.stringify({ chat_id: this.chatService.chatId })
      });

      if (!response.ok) {
        throw new Error('Failed to get Speechmatics JWT');
      }

      const data = await response.json();
      this.speechmaticsHandler = new SpeechmaticsHandler(this.chatInput, this.languageCode);
      await this.speechmaticsHandler.setupClient(data.jwt);

      this.setupBasicRecorder(this.stream);
    } catch (error) {
      console.error('Error setting up Speechmatics:', error);
      throw error;
    }
  }
}

export default AudioRecordingService;
